import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";

export const EMPTY_TAG_CONTENT = { header: "", footer: "", image_id: "", image_position: "above" };
const content = (d) => ({ header: d.header, footer: d.footer, image_id: d.image_id, image_position: d.image_position });
export function validTagSettings(d, year) {
  return d?.year === Number(year) && Number.isInteger(d.revision) && d.revision >= 0 &&
    typeof d.editable === "boolean" && typeof d.header === "string" && typeof d.footer === "string" &&
    typeof d.image_id === "string" && /^(?:[a-f0-9]{32})?$/.test(d.image_id) &&
    ["above", "below"].includes(d.image_position);
}

export default function useSharingTreeTagContent(year) {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(EMPTY_TAG_CONTENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [conflict, setConflict] = useState(false);
  const [retry, setRetry] = useState(0);
  const scope = useRef(0);
  const pending = useRef(false);
  const ready = state?.year === Number(year);
  const settings = ready ? draft : EMPTY_TAG_CONTENT;
  const dirty = ready && JSON.stringify(content(state)) !== JSON.stringify(draft);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  useEffect(() => {
    const generation = ++scope.current;
    const ctrl = new AbortController();
    pending.current = false;
    setLoading(true); setError(""); setMessage(""); setConflict(false); setBusy("");
    api.get("/sharing-tree/tag-settings", { params: { year }, signal: ctrl.signal })
      .then(({ data }) => {
        if (generation !== scope.current) return;
        if (!validTagSettings(data, year)) throw new Error("Invalid tag settings response. Reload before printing.");
        setState(data); setDraft(content(data)); setLoading(false);
      }).catch(e => {
        if (generation !== scope.current || ctrl.signal.aborted) return;
        setError(formatApiError(e)); setLoading(false); setState(null);
      });
    return () => { scope.current += 1; ctrl.abort(); };
  }, [year, retry]);

  useEffect(() => {
    const focus = () => {
      if (!dirtyRef.current && !pending.current) setRetry(n => n + 1);
    };
    window.addEventListener("focus", focus);
    return () => window.removeEventListener("focus", focus);
  }, []);

  function onChange(patch) {
    if (!ready || !state.editable || pending.current || loading) return;
    setDraft(d => ({ ...d, ...patch })); setMessage("");
  }
  function onReload() {
    if (pending.current) return;
    if (dirty && !window.confirm("Discard your unsaved tag content and load the saved settings?")) return;
    setRetry(n => n + 1);
  }
  async function onSave() {
    if (!ready || !state.editable || pending.current || !dirty || loading || conflict) return;
    const generation = scope.current;
    pending.current = true; setBusy("saving"); setError(""); setMessage("");
    try {
      const { data } = await api.put("/sharing-tree/tag-settings", { ...draft, revision: state.revision }, { params: { year } });
      if (generation !== scope.current) return;
      if (!validTagSettings(data, year)) throw new Error("Save could not be confirmed. Reload the saved settings.");
      setState(data); setDraft(content(data)); setMessage(`Saved for ${year}. Available to other staff.`);
    } catch (e) {
      if (generation === scope.current) {
        setError(formatApiError(e));
        if (e?.response?.status === 409) setConflict(true);
      }
    } finally {
      if (generation === scope.current) { pending.current = false; setBusy(""); }
    }
  }
  async function artwork(file, logo = false) {
    if (!ready || !state.editable || pending.current || loading) return;
    if (!logo && (!file || file.size > 3 * 1024 * 1024 || !["image/png", "image/jpeg", "image/webp"].includes(file.type))) {
      setError("Choose a PNG, JPEG, or WebP image no larger than 3 MB."); return;
    }
    const generation = scope.current;
    pending.current = true; setBusy("uploading"); setError(""); setMessage("");
    try {
      const body = new FormData();
      if (file) body.append("file", file);
      const { data } = await api.post(`/sharing-tree/tag-artwork${logo ? "/logo" : ""}`, body, { params: { year } });
      if (generation !== scope.current) return;
      if (data?.year !== Number(year) || !/^[a-f0-9]{32}$/.test(data.image_id || "")) throw new Error("Artwork upload could not be confirmed.");
      setDraft(d => ({ ...d, image_id: data.image_id }));
      setMessage("Artwork is ready. Save for this year to share it with staff.");
    } catch (e) {
      if (generation === scope.current) setError(formatApiError(e));
    } finally {
      if (generation === scope.current) { pending.current = false; setBusy(""); }
    }
  }
  return { settings, loading, loadFailed: !loading && !ready, error, message, dirty, conflict,
    canEdit: ready && state.editable, saving: busy === "saving", uploading: busy === "uploading",
    onChange, onSave, onReload, onUpload: file => artwork(file), onUseLogo: () => artwork(null, true) };
}

export function useTagArtwork(year, imageId) {
  const [image, setImage] = useState(null);
  const [retry, setRetry] = useState(0);
  const reload = useCallback(() => setRetry(n => n + 1), []);
  useEffect(() => {
    if (!imageId) { setImage(null); return undefined; }
    let cancelled = false, url = "";
    const ctrl = new AbortController();
    setImage(null);
    api.get(`/sharing-tree/tag-artwork/${imageId}`, { params: { year }, responseType: "blob", signal: ctrl.signal })
      .then(async ({ data }) => {
        if (cancelled) return;
        url = URL.createObjectURL(data);
        const decoder = new Image();
        await new Promise((resolve, reject) => {
          decoder.onload = resolve;
          decoder.onerror = () => reject(new Error("Image decoding failed."));
          decoder.src = url;
        });
        if (decoder.decode) await decoder.decode();
        if (!cancelled) setImage({ year, id: imageId, src: url });
      }).catch(() => {
        if (!cancelled) setImage({ year, id: imageId, error: true });
      });
    return () => { cancelled = true; ctrl.abort(); if (url) URL.revokeObjectURL(url); };
  }, [year, imageId, retry]);
  const current = image?.id === imageId && image?.year === year ? image : null;
  return { src: current?.src || "", loading: !!imageId && !current, error: !!current?.error, reload };
}
